import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Wrench, Phone, FileText, CheckCircle, Settings, FileBarChart, FolderOpen, LogOut, Receipt, Package, Calendar, Clock, StickyNote, Folder, Wallet, TrendingUp, TrendingDown, Key, AlertTriangle, Bell, Search, X, Plus, Edit2, Trash2, Pin, PinOff, AlarmClock, GripVertical, Smartphone } from 'lucide-react';
import PasswordDialog from '../components/PasswordDialog';
import ManagerPasswordDialog from '../components/ManagerPasswordDialog';
import { getDailyTasks, toggleTaskCompletion, getDailyLogs, getProjects, sendDebtAlert, getChecksReport, globalSearch, getNotes, createNote, updateNote, deleteNote, reorderNotes, getSupportTickets, getPriceItems, updatePriceItem, authLogout, authLogoutAll } from '../api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const Home = () => {
  const navigate = useNavigate();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showManagerPasswordDialog, setShowManagerPasswordDialog] = useState(false);
  const [pendingRoute, setPendingRoute] = useState('');
  const [managerName, setManagerName] = useState('');
  const [todayTasks, setTodayTasks] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [emailAlertSent, setEmailAlertSent] = useState(false);
  const [tomorrowChecks, setTomorrowChecks] = useState([]);
  const [checkAlertDismissed, setCheckAlertDismissed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const [notes, setNotes] = useState([]);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteColor, setNoteColor] = useState('#FEF3C7');
  const [notePinned, setNotePinned] = useState(false);
  const [noteReminder, setNoteReminder] = useState('');
  const [activeReminders, setActiveReminders] = useState([]);
  const [draggedNoteId, setDraggedNoteId] = useState(null);
  const [dragOverNoteId, setDragOverNoteId] = useState(null);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [lowProfitItems, setLowProfitItems] = useState([]);
  const [lowProfitThreshold, setLowProfitThreshold] = useState(15);
  const [lowProfitDismissed, setLowProfitDismissed] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [editingCostId, setEditingCostId] = useState(null);
  const [editingCostValue, setEditingCostValue] = useState('');
  const [savingCost, setSavingCost] = useState(false);

  const NOTE_COLORS = ['#FEF3C7', '#DBEAFE', '#D1FAE5', '#FCE7F3', '#EDE9FE', '#FEE2E2', '#E0F2FE', '#FEF9C3'];

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTodayTasks = async () => {
    try {
      const today = new Date();
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const tasks = await getDailyTasks(todayString);
      setTodayTasks(tasks);
    } catch (error) {
      console.error('Error loading today tasks:', error);
    }
  };

  const loadDebtors = async () => {
    try {
      const logs = await getDailyLogs();
      const projects = await getProjects();
      const debtorLogs = logs.filter(log => !log.accountClosed).map(log => {
        const total = (log.workItems || []).reduce((s, i) => {
          const qty = parseFloat(i.quantity) || 0;
          const price = parseFloat(i.transactionAmount) || 0;
          return s + (qty * price);
        }, 0) * (1 + (parseFloat(log.vatRate) || 0) / 100);
        const paid = (log.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
        return { id: log.id, name: log.customerName, debt: total - paid, type: 'daily_log' };
      });
      const debtorProjects = projects.filter(p => !p.accountClosed).map(p => {
        const total = (p.items || []).reduce((s, i) => {
          const qty = parseFloat(i.quantity) || 0;
          const price = parseFloat(i.transactionAmount) || 0;
          return s + (qty * price);
        }, 0);
        const paid = (p.payments || []).reduce((s, pp) => s + (parseFloat(pp.amount) || 0), 0);
        return { id: p.id, name: p.projectName, debt: total - paid, type: 'project' };
      });
      setDebtors([...debtorLogs, ...debtorProjects].filter(d => d.debt > 0));
    } catch (error) {
      console.error('Error loading debtors:', error);
    }
  };

  const loadTomorrowChecks = async () => {
    try {
      const checks = await getChecksReport();
      const today = new Date();
      const dates = [];
      for (let i = 0; i <= 3; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        dates.push(d.toISOString().split('T')[0]);
      }
      const upcoming = checks.filter(c => !c.deposited && dates.includes(c.checkDate));
      setTomorrowChecks(upcoming);
    } catch (error) {
      console.error('Error loading checks:', error);
    }
  };

  const loadNotes = async () => {
    try {
      const data = await getNotes();
      setNotes(data);
      // Check for due reminders
      const now = new Date();
      const due = data.filter(n => 
        n.reminder && !n.reminderDismissed && new Date(n.reminder) <= now
      ).map(n => n.id);
      setActiveReminders(due);
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const loadOpenTickets = async () => {
    try {
      const data = await getSupportTickets();
      setOpenTicketsCount(data.filter(t => t.status !== 'closed').length);
    } catch (error) {
      console.error('Error loading tickets:', error);
    }
  };

  const loadLowProfitItems = async () => {
    try {
      const saved = parseFloat(localStorage.getItem('lowProfitThreshold'));
      const threshold = isNaN(saved) ? 15 : saved;
      setLowProfitThreshold(threshold);
      const items = await getPriceItems('__global__');
      const low = (items || [])
        .map(it => {
          const price = parseFloat(it.price) || 0;
          const cost = parseFloat(it.costPrice) || 0;
          const profit = cost ? ((price - cost) / cost) * 100 : null;
          return { ...it, profit };
        })
        .filter(it => it.profit === null || it.profit < threshold);
      setLowProfitItems(low);
    } catch (error) {
      console.error('Error loading low profit items:', error);
    }
  };

  const handleStartEditPrice = (item) => {
    setEditingPriceId(item.id);
    setEditingPriceValue(item.price || '');
  };

  const handleSavePrice = async (item) => {
    if (savingPrice) return;
    setSavingPrice(true);
    try {
      await updatePriceItem(item.id, {
        workId: item.workId,
        partName: item.partName,
        price: String(editingPriceValue),
        costPrice: item.costPrice || '',
        supplier: item.supplier || '',
        invoiceId: item.invoiceId || '',
        date: item.date || ''
      });
      setEditingPriceId(null);
      setEditingPriceValue('');
      await loadLowProfitItems();
      toast({ title: 'מחיר עודכן', description: `המחיר של "${item.partName}" עודכן` });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן מחיר', variant: 'destructive' });
    } finally {
      setSavingPrice(false);
    }
  };

  const handleStartEditCost = (item) => {
    setEditingCostId(item.id);
    setEditingCostValue(item.costPrice || '');
  };

  const handleSaveCost = async (item) => {
    if (savingCost) return;
    setSavingCost(true);
    try {
      await updatePriceItem(item.id, {
        workId: item.workId,
        partName: item.partName,
        price: item.price || '',
        costPrice: String(editingCostValue),
        supplier: item.supplier || '',
        invoiceId: item.invoiceId || '',
        date: item.date || ''
      });
      setEditingCostId(null);
      setEditingCostValue('');
      await loadLowProfitItems();
      toast({ title: 'עלות עודכנה', description: `מחיר העלות של "${item.partName}" עודכן` });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן עלות', variant: 'destructive' });
    } finally {
      setSavingCost(false);
    }
  };

  useEffect(() => {
    // Check if logged in
    const loggedIn = localStorage.getItem('managerLoggedIn');
    const name = localStorage.getItem('managerName');
    
    if (loggedIn !== 'true') {
      navigate('/');
    } else {
      setManagerName(name || 'מנהל');
      loadTodayTasks();
      loadDebtors().then(() => {
        if (!emailAlertSent) {
          sendDebtAlert().then(() => setEmailAlertSent(true)).catch(() => {});
        }
      });
      loadTomorrowChecks();
      loadNotes();
      loadOpenTickets();
      loadLowProfitItems();
    }
  }, [navigate]);

  const handleToggleTask = async (taskId) => {
    try {
      await toggleTaskCompletion(taskId);
      await loadTodayTasks();
    } catch (error) {
      console.error('Error toggling task:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לעדכן משימה',
        variant: 'destructive'
      });
    }
  };

  const handleLogout = async () => {
    // Invalidate the server-side session and clear the httpOnly cookie
    await authLogout();
    // Clear ALL client auth state so revisiting the URL cannot auto-login
    localStorage.removeItem('managerLoggedIn');
    localStorage.removeItem('managerName');
    localStorage.removeItem('googleAuth');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('authToken');
    navigate('/', { replace: true });
  };

  const handleLogoutAllDevices = async () => {
    if (!window.confirm('לנתק את כל המכשירים המחוברים? תצטרך להתחבר מחדש בכל מכשיר.')) return;
    await authLogoutAll();
    localStorage.removeItem('managerLoggedIn');
    localStorage.removeItem('managerName');
    localStorage.removeItem('googleAuth');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('authToken');
    toast({ title: 'נותקת מכל המכשירים', description: 'כל הסשנים הפעילים בוטלו' });
    navigate('/', { replace: true });
  };

  const handleServiceCallClick = () => {
    setShowManagerPasswordDialog(true);
  };

  const handleDealClosureClick = () => {
    setPendingRoute('/deal-closure');
    setShowPasswordDialog(true);
  };

  const handleDailyLogClick = () => {
    setPendingRoute('/daily-log');
    setShowPasswordDialog(true);
  };

  const handlePasswordSuccess = () => {
    setShowPasswordDialog(false);
    if (pendingRoute) {
      navigate(pendingRoute);
      setPendingRoute('');
    }
  };

  const handleManagerPasswordSuccess = () => {
    setShowManagerPasswordDialog(false);
    navigate('/new-service-call');
  };

  const handleOpenAddNote = () => {
    setEditingNote(null);
    setNoteText('');
    setNoteColor('#FEF3C7');
    setNotePinned(false);
    setNoteReminder('');
    setShowNoteDialog(true);
  };

  const handleOpenEditNote = (note) => {
    setEditingNote(note);
    setNoteText(note.text);
    setNoteColor(note.color || '#FEF3C7');
    setNotePinned(note.pinned || false);
    setNoteReminder(note.reminder || '');
    setShowNoteDialog(true);
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      toast({ title: 'שגיאה', description: 'נא לכתוב הערה', variant: 'destructive' });
      return;
    }
    try {
      if (editingNote) {
        await updateNote(editingNote.id, { text: noteText, color: noteColor, pinned: notePinned, reminder: noteReminder || null });
      } else {
        await createNote(noteText, noteColor, notePinned, noteReminder || null);
      }
      setShowNoteDialog(false);
      loadNotes();
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לשמור הערה', variant: 'destructive' });
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await updateNote(note.id, { pinned: !note.pinned });
      loadNotes();
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן', variant: 'destructive' });
    }
  };

  const handleDismissReminder = async (noteId) => {
    try {
      await updateNote(noteId, { reminderDismissed: true });
      setActiveReminders(prev => prev.filter(id => id !== noteId));
      loadNotes();
    } catch (error) {
      console.error('Error dismissing reminder:', error);
    }
  };

  const handleDragStart = (e, noteId) => {
    setDraggedNoteId(noteId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', noteId);
    // Make the drag image slightly transparent
    if (e.target) {
      setTimeout(() => { e.target.style.opacity = '0.5'; }, 0);
    }
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedNoteId(null);
    setDragOverNoteId(null);
  };

  const handleDragOver = (e, noteId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (noteId !== draggedNoteId) {
      setDragOverNoteId(noteId);
    }
  };

  const handleDrop = async (e, targetNoteId) => {
    e.preventDefault();
    const sourceId = draggedNoteId;
    setDraggedNoteId(null);
    setDragOverNoteId(null);
    if (!sourceId || sourceId === targetNoteId) return;

    // Reorder locally first for instant feedback
    const oldList = [...notes];
    const sourceIdx = oldList.findIndex(n => n.id === sourceId);
    const targetIdx = oldList.findIndex(n => n.id === targetNoteId);
    if (sourceIdx === -1 || targetIdx === -1) return;

    const [moved] = oldList.splice(sourceIdx, 1);
    oldList.splice(targetIdx, 0, moved);
    setNotes(oldList);

    // Persist new order
    try {
      await reorderNotes(oldList.map(n => n.id));
    } catch (error) {
      // Revert on failure
      loadNotes();
      toast({ title: 'שגיאה', description: 'לא ניתן לשנות סדר', variant: 'destructive' });
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(noteId);
      loadNotes();
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן למחוק', variant: 'destructive' });
    }
  };

  const handleCompleteNote = async (noteId) => {
    try {
      await updateNote(noteId, { completed: true });
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast({ title: 'הועבר להיסטוריה', description: 'ההערה סומנה כבוצעה ועברה להיסטוריית הערות בדף מנהל' });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן', variant: 'destructive' });
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    const trimmed = query.trim();
    // Allow single-digit numeric queries (call number search); otherwise require 2+ chars
    if (!trimmed || (trimmed.length < 2 && !/^\d+$/.test(trimmed))) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    setIsSearching(true);
    setShowSearchResults(true);
    try {
      const data = await globalSearch(query);
      setSearchResults(data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const typeColors = {
    daily_log: 'bg-blue-100 text-blue-700',
    service_call: 'bg-orange-100 text-orange-700',
    project: 'bg-purple-100 text-purple-700',
    customer: 'bg-teal-100 text-teal-700',
    work: 'bg-indigo-100 text-indigo-700',
    product_key: 'bg-pink-100 text-pink-700',
    deal: 'bg-green-100 text-green-700',
    note: 'bg-amber-100 text-amber-700'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" dir="rtl">
      <div className="container mx-auto px-4 py-16">
        {/* Header with Logout */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <div className="text-lg font-semibold text-gray-700">
              שלום, {managerName} 👋
            </div>
            <div className="text-sm text-gray-600">
              {new Date().toLocaleDateString('he-IL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleLogoutAllDevices}
              variant="outline"
              className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50"
              data-testid="logout-all-devices-btn"
              title="מתנתק מכל המכשירים שבהם אתה מחובר"
            >
              <Smartphone className="ml-2 w-5 h-5" />
              התנתק מכל המכשירים
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-2 border-red-500 text-red-600 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="ml-2 w-5 h-5" />
              יציאה
            </Button>
          </div>
        </div>

        {/* Today's Tasks - Vertical Ticker */}
        {todayTasks.filter(t => !t.completed).length > 0 && (
          <div className="max-w-4xl mx-auto mb-6">
            <div className="bg-white rounded-lg shadow-md p-3 border-t-2 border-blue-500 overflow-hidden">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-base font-bold text-gray-800 flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  משימות היום ({new Date().toLocaleDateString('he-IL', { day: 'numeric', month: 'long' })})
                </h2>
                <Button
                  onClick={() => navigate('/daily-journal')}
                  variant="outline"
                  size="sm"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50 text-xs h-7 px-2"
                >
                  עבור ליומן
                </Button>
              </div>
              
              {/* Vertical Ticker */}
              <div className="relative h-32 overflow-hidden">
                <div className="ticker-vertical absolute w-full">
                  {todayTasks.filter(t => !t.completed).map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-2 p-2 mb-2 rounded-md border bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
                    >
                      <Checkbox
                        checked={task.completed}
                        onCheckedChange={() => handleToggleTask(task.id)}
                        className="h-4 w-4"
                      />
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800">
                          {task.title}
                        </span>
                        {task.time && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" />
                            {task.time}
                          </span>
                        )}
                        {task.description && (
                          <span className="text-xs text-gray-500">• {task.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-2 text-xs text-gray-600 flex justify-between items-center bg-gray-50 p-2 rounded">
                <span>סה&quot;כ: <strong>{todayTasks.length}</strong></span>
                <span>הושלמו: <strong className="text-green-600">{todayTasks.filter(t => t.completed).length}</strong></span>
                <span>ממתינות: <strong className="text-orange-600">{todayTasks.filter(t => !t.completed).length}</strong></span>
              </div>
            </div>
            
            {/* Ticker Animation CSS */}
            <style>{`
              @keyframes ticker-scroll {
                0% { transform: translateY(0); }
                100% { transform: translateY(-50%); }
              }
              .ticker-vertical {
                animation: ticker-scroll 15s linear infinite;
              }
              .ticker-vertical:hover {
                animation-play-state: paused;
              }
            `}</style>
          </div>
        )}

        <div className="max-w-4xl mx-auto text-center space-y-12">
          {/* Header */}
          <div className="space-y-6">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                <Wrench className="w-16 h-16 text-white" />
              </div>
            </div>
            <h1 className="text-6xl font-extrabold text-gray-900 mb-6">
              אימן שירותי מחשוב
            </h1>
            <div className="space-y-2">
              <p className="text-xl font-semibold text-indigo-700">
                aiman@aimanpc.com | 0525767555
              </p>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
                התקנת רשתות תקשורת מכירה והתקנת מחשבים נייחים וניידים מצלמות ומעגל סגור ואינטרקומים
              </p>
            </div>
          </div>

          {/* Global Search */}
          <div className="relative max-w-2xl mx-auto mb-4" data-testid="global-search-container" ref={searchRef}>
            <div className="relative">
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                placeholder="חיפוש חכם - לקוחות, עבודות, קריאות שירות, פרויקטים..."
                className="w-full pr-12 pl-10 py-4 text-base border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-all bg-white shadow-md hover:shadow-lg"
                data-testid="global-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchResults([]); setShowSearchResults(false); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="global-search-clear"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {showSearchResults && (
              <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-96 overflow-y-auto" data-testid="global-search-results">
                {isSearching ? (
                  <div className="p-6 text-center text-gray-500">מחפש...</div>
                ) : searchResults.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    {searchQuery.length >= 2 ? 'לא נמצאו תוצאות' : 'הקלד לפחות 2 תווים לחיפוש'}
                  </div>
                ) : (
                  <div>
                    <div className="px-4 py-2 bg-gray-50 border-b text-sm text-gray-600 font-semibold">
                      נמצאו {searchResults.length} תוצאות
                    </div>
                    {searchResults.map((result, idx) => (
                      <button
                        key={`${result.type}-${result.id}-${idx}`}
                        onClick={() => {
                          setShowSearchResults(false);
                          setSearchQuery('');
                          navigate(result.link);
                        }}
                        className="w-full text-right px-4 py-3 hover:bg-indigo-50 transition-colors border-b last:border-b-0 flex items-center gap-3"
                        data-testid={`search-result-${idx}`}
                      >
                        <span className={`text-xs px-2 py-1 rounded-full font-bold whitespace-nowrap ${typeColors[result.type] || 'bg-gray-100 text-gray-700'}`}>
                          {result.typeLabel}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{result.title}</div>
                          {result.subtitle && (
                            <div className="text-xs text-gray-500 truncate">{result.subtitle}</div>
                          )}
                        </div>
                        {result.date && (
                          <span className="text-xs text-gray-400 whitespace-nowrap">{result.date}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 my-12">
            <div className="p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer transform hover:scale-105" onClick={handleServiceCallClick}>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">מכללה טכנולוגית</h3>
              <p className="text-gray-600 text-sm">פתח קריאת שירות תוך שניות</p>
            </div>
            <div className="p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer transform hover:scale-105" onClick={handleDailyLogClick}>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">רישום יומי</h3>
              <p className="text-gray-600 text-sm">תיעוד עבודות ועסקאות יומיות</p>
            </div>
            <div className="p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer transform hover:scale-105" onClick={handleDealClosureClick}>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">סגירת עסקה</h3>
              <p className="text-gray-600 text-sm">רישום וניהול סגירת עסקאות</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
            <Button
              onClick={handleServiceCallClick}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <Wrench className="ml-2 w-5 h-5" />
              מכללה טכנולוגית
            </Button>
            <Button
              onClick={handleDailyLogClick}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <FileText className="ml-2 w-5 h-5" />
              רישום יומי
            </Button>
            <Button
              onClick={handleDealClosureClick}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <CheckCircle className="ml-2 w-5 h-5" />
              סגירת עסקה חדשה
            </Button>
            <Button
              onClick={() => navigate('/reports')}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <FileText className="ml-2 w-5 h-5" />
              דווח
            </Button>
            <Button
              onClick={() => navigate('/purchase-invoices')}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <Receipt className="ml-2 w-5 h-5" />
              חשבוניות קנייה
            </Button>
            <Button
              onClick={() => navigate('/daily-journal')}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
            >
              <Calendar className="ml-2 w-5 h-5" />
              יומן
            </Button>
            <Button
              onClick={() => navigate('/product-key')}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
              data-testid="product-key-button"
            >
              <Key className="ml-2 w-5 h-5" />
              מפתח מוצר
            </Button>
            <Button
              onClick={() => navigate('/customers')}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              size="lg"
              data-testid="customers-button"
            >
              <FileText className="ml-2 w-5 h-5" />
              לקוחות
            </Button>
            <Button
              onClick={() => navigate('/manager-login')}
              variant="outline"
              className="px-8 py-6 text-lg font-semibold border-2 border-purple-600 text-purple-600 hover:bg-purple-50 transition-all duration-300"
              size="lg"
            >
              <Settings className="ml-2 w-5 h-5" />
              מנהל
            </Button>
            <Button
              onClick={() => navigate('/support-tickets')}
              className="px-8 py-6 text-lg font-semibold bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 relative"
              size="lg"
              data-testid="support-tickets-button"
            >
              <Phone className="ml-2 w-5 h-5" />
              קריאות שירות
              {openTicketsCount > 0 && (
                <span className="absolute -top-2 -left-2 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-lg animate-pulse" data-testid="open-tickets-badge">
                  {openTicketsCount}
                </span>
              )}
            </Button>
          </div>

          {/* Sticky Notes Section */}
          <div className="max-w-4xl mx-auto mt-10 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <StickyNote className="w-5 h-5 text-amber-500" />
                הערות
              </h2>
              <Button
                onClick={handleOpenAddNote}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white shadow-md"
                size="sm"
                data-testid="add-note-btn"
              >
                <Plus className="ml-1 w-4 h-4" />
                הערה חדשה
              </Button>
            </div>

            {/* Reminder Alerts */}
            {activeReminders.length > 0 && (
              <div className="mb-4 space-y-2">
                {notes.filter(n => activeReminders.includes(n.id)).map(note => (
                  <div key={`reminder-${note.id}`} className="flex items-center gap-3 bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-300 rounded-lg p-3 animate-pulse-slow" data-testid={`reminder-alert-${note.id}`}>
                    <AlarmClock className="w-5 h-5 text-orange-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-orange-900">תזכורת!</p>
                      <p className="text-sm text-gray-800 truncate">{note.text}</p>
                    </div>
                    <button
                      onClick={() => handleDismissReminder(note.id)}
                      className="text-xs bg-white text-orange-700 px-3 py-1.5 rounded-lg font-semibold hover:bg-orange-50 border border-orange-300 flex-shrink-0"
                      data-testid={`dismiss-reminder-${note.id}`}
                    >
                      הבנתי
                    </button>
                  </div>
                ))}
              </div>
            )}

            {notes.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {notes.map((note, idx) => (
                  <div
                    key={note.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, note.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, note.id)}
                    onDrop={(e) => handleDrop(e, note.id)}
                    className={`relative group rounded-lg p-4 shadow-md hover:shadow-lg transition-all min-h-[120px] flex flex-col cursor-grab active:cursor-grabbing ${note.pinned ? 'ring-2 ring-amber-400' : ''} ${activeReminders.includes(note.id) ? 'ring-2 ring-orange-400 animate-pulse-slow' : ''} ${dragOverNoteId === note.id && draggedNoteId !== note.id ? 'ring-2 ring-indigo-400 scale-105' : ''} ${draggedNoteId === note.id ? 'opacity-50' : ''}`}
                    style={{ backgroundColor: note.color || '#FEF3C7' }}
                    data-testid={`note-${note.id}`}
                  >
                    {/* Drag handle */}
                    <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5">
                      {note.pinned && <Pin className="w-3.5 h-3.5 text-amber-600" />}
                      <GripVertical className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-60 transition-opacity cursor-grab" />
                    </div>
                    {/* Action buttons */}
                    <div className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button
                        onClick={() => handleTogglePin(note)}
                        className={`p-1 rounded transition-colors ${note.pinned ? 'bg-amber-200 text-amber-700' : 'bg-white/70 hover:bg-white text-gray-600 hover:text-amber-600'}`}
                        title={note.pinned ? 'בטל הצמדה' : 'הצמד'}
                        data-testid={`pin-note-${note.id}`}
                      >
                        {note.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => handleOpenEditNote(note)}
                        className="p-1 rounded bg-white/70 hover:bg-white text-gray-600 hover:text-blue-600 transition-colors"
                        data-testid={`edit-note-${note.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-1 rounded bg-white/70 hover:bg-white text-gray-600 hover:text-red-600 transition-colors"
                        data-testid={`delete-note-${note.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap flex-1 leading-relaxed pt-1">{note.text}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        {/* Complete checkbox */}
                        <button
                          onClick={() => handleCompleteNote(note.id)}
                          className="w-5 h-5 rounded border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 flex items-center justify-center transition-all text-transparent hover:text-green-500"
                          title="סמן כבוצע והעבר להיסטוריה"
                          data-testid={`complete-note-${note.id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                        </button>
                        {note.reminder && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                          !note.reminderDismissed && new Date(note.reminder) <= new Date()
                            ? 'bg-orange-200 text-orange-800 font-bold'
                            : note.reminderDismissed
                            ? 'bg-gray-200 text-gray-500 line-through'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          <AlarmClock className="w-2.5 h-2.5" />
                          {new Date(note.reminder).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      </div>
                      <div className="text-[10px] text-gray-500 text-left" dir="ltr">
                        {new Date(note.updatedAt || note.createdAt).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
                {/* Add Note Card */}
                <button
                  onClick={handleOpenAddNote}
                  className="rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-amber-400 min-h-[120px] flex flex-col items-center justify-center text-gray-400 hover:text-amber-500 transition-all hover:bg-amber-50/50 cursor-pointer"
                  data-testid="add-note-card"
                >
                  <Plus className="w-8 h-8 mb-1" />
                  <span className="text-sm font-medium">הוסף הערה</span>
                </button>
              </div>
            ) : (
              <button
                onClick={handleOpenAddNote}
                className="w-full rounded-lg p-6 border-2 border-dashed border-gray-300 hover:border-amber-400 flex flex-col items-center justify-center text-gray-400 hover:text-amber-500 transition-all hover:bg-amber-50/50 cursor-pointer"
                data-testid="add-first-note"
              >
                <StickyNote className="w-10 h-10 mb-2" />
                <span className="text-base font-medium">לחץ להוספת הערה ראשונה</span>
              </button>
            )}
          </div>
        </div>

        {/* Note Dialog */}
        <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
          <DialogContent className="max-w-md" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <StickyNote className="w-5 h-5 text-amber-500" />
                {editingNote ? 'עריכת הערה' : 'הערה חדשה'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="כתוב את ההערה כאן..."
                className="w-full min-h-[120px] p-3 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none resize-none text-sm"
                autoFocus
                data-testid="note-text-input"
              />
              {/* Reminder */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5 block">
                  <AlarmClock className="w-4 h-4 text-orange-500" />
                  תזכורת
                </label>
                <input
                  type="datetime-local"
                  value={noteReminder}
                  onChange={(e) => setNoteReminder(e.target.value)}
                  className="w-full p-2.5 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:outline-none text-sm"
                  data-testid="note-reminder-input"
                />
                {noteReminder && (
                  <button
                    onClick={() => setNoteReminder('')}
                    className="text-xs text-red-500 hover:text-red-700 mt-1"
                  >
                    הסר תזכורת
                  </button>
                )}
              </div>
              {/* Pin Toggle */}
              <div
                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${notePinned ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => setNotePinned(!notePinned)}
                data-testid="note-pin-toggle"
              >
                <Pin className={`w-5 h-5 ${notePinned ? 'text-amber-600' : 'text-gray-400'}`} />
                <div className="flex-1">
                  <span className={`text-sm font-semibold ${notePinned ? 'text-amber-800' : 'text-gray-600'}`}>
                    {notePinned ? 'הערה מוצמדת' : 'הצמד הערה'}
                  </span>
                  <p className="text-xs text-gray-500">הערות מוצמדות מופיעות תמיד ראשונות</p>
                </div>
                <div className={`w-10 h-6 rounded-full transition-colors ${notePinned ? 'bg-amber-500' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow mt-0.5 transition-transform ${notePinned ? 'mr-0.5' : 'mr-[18px]'}`} />
                </div>
              </div>
              {/* Color */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">צבע</label>
                <div className="flex gap-2 flex-wrap">
                  {NOTE_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNoteColor(c)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${noteColor === c ? 'border-gray-800 scale-110 ring-2 ring-gray-300' : 'border-gray-200 hover:border-gray-400'}`}
                      style={{ backgroundColor: c }}
                      data-testid={`note-color-${c}`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveNote}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
                  data-testid="save-note-btn"
                >
                  {editingNote ? 'עדכן' : 'הוסף'}
                </Button>
                <Button variant="outline" onClick={() => setShowNoteDialog(false)} className="px-6">
                  ביטול
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Password Dialogs */}
        <PasswordDialog
          open={showPasswordDialog}
          onOpenChange={setShowPasswordDialog}
          onSuccess={handlePasswordSuccess}
        />
        <ManagerPasswordDialog
          open={showManagerPasswordDialog}
          onOpenChange={setShowManagerPasswordDialog}
          onSuccess={handleManagerPasswordSuccess}
        />

        {/* AIMAN Folder Section - At Bottom */}
        <div className="max-w-4xl mx-auto mt-8 mb-6">

          {/* Debt Alert Banner */}
          {debtors.length > 0 && !alertDismissed && (
            <div className="mb-4" data-testid="debt-alert-banner">
              <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg shadow-lg p-4 text-white relative animate-pulse-slow">
                <button
                  onClick={() => setAlertDismissed(true)}
                  className="absolute top-2 left-2 text-white/70 hover:text-white text-xl font-bold w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                  data-testid="dismiss-alert-btn"
                >
                  ×
                </button>
                <div className="flex items-center gap-3">
                  <Bell className="w-8 h-8 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold">
                      התראת חייבים: {debtors.length} לקוחות עם חוב פתוח
                    </h3>
                    <p className="text-sm text-white/90">
                      סה&quot;כ חוב: ₪{debtors.reduce((s, d) => s + d.debt, 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/debtors-report')}
                    className="bg-white text-red-600 px-4 py-2 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors flex-shrink-0"
                    data-testid="alert-view-debtors-btn"
                  >
                    צפה בדווח
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tomorrow Checks Alert Banner */}
          {tomorrowChecks.length > 0 && !checkAlertDismissed && (
            <div className="mb-4" data-testid="checks-alert-banner">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-500 rounded-lg shadow-lg p-4 text-white relative">
                <button
                  onClick={() => setCheckAlertDismissed(true)}
                  className="absolute top-2 left-2 text-white/70 hover:text-white text-xl font-bold w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                  data-testid="dismiss-checks-alert-btn"
                >
                  ×
                </button>
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold">
                      התראת שיקים: {tomorrowChecks.length} שיקים לפירעון ב-3 הימים הקרובים
                    </h3>
                    <div className="mt-2 space-y-1">
                      {tomorrowChecks.map((c, i) => (
                        <div key={i} className="text-sm text-white/90 flex items-center gap-2">
                          <span className="font-semibold">{c.customerName}</span>
                          <span>-</span>
                          <span>₪{(parseFloat(c.amount) || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}</span>
                          <span className="text-white/70">({c.bankName} #{c.checkNumber})</span>
                          <span className="text-white/60 text-xs">| {new Date(c.checkDate).toLocaleDateString('he-IL', { day: 'numeric', month: 'short' })}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/checks-report')}
                    className="bg-white text-amber-600 px-4 py-2 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors flex-shrink-0"
                    data-testid="alert-view-checks-btn"
                  >
                    דוח שיקים
                  </button>
                </div>
              </div>
            </div>
          )}

          <style>{`
            @keyframes pulse-slow {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.92; }
            }
            .animate-pulse-slow {
              animation: pulse-slow 3s ease-in-out infinite;
            }
          `}</style>

          {/* Low Profit Alert Banner */}
          {lowProfitItems.length > 0 && !lowProfitDismissed && (() => {
            const belowItems = lowProfitItems.filter(it => it.profit !== null);
            const missingItems = lowProfitItems.filter(it => it.profit === null);

            const renderPriceCell = (it, labelColorClass) => {
              if (editingPriceId === it.id) {
                return (
                  <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="number"
                      value={editingPriceValue}
                      onChange={(e) => setEditingPriceValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSavePrice(it); if (e.key === 'Escape') setEditingPriceId(null); }}
                      className="w-20 text-xs text-gray-900 rounded px-1.5 py-0.5 border border-white/40 focus:outline-none"
                      autoFocus
                      data-testid={`low-profit-price-input-${it.id}`}
                    />
                    <button
                      onClick={() => handleSavePrice(it)}
                      disabled={savingPrice}
                      className="text-xs bg-white text-emerald-700 rounded px-2 py-0.5 font-bold hover:bg-emerald-50 disabled:opacity-50"
                      data-testid={`low-profit-save-${it.id}`}
                    >
                      שמור
                    </button>
                    <button
                      onClick={() => setEditingPriceId(null)}
                      className="text-xs text-white/80 hover:text-white"
                      data-testid={`low-profit-cancel-${it.id}`}
                    >
                      ביטול
                    </button>
                  </span>
                );
              }
              return (
                <span className={`font-semibold ${labelColorClass}`}>
                  {it.profit === null ? 'רווח לא ידוע' : `${it.profit.toFixed(1)}%`}
                </span>
              );
            };

            const renderChip = (it, labelColorClass) => (
              <span key={it.id} className="text-xs bg-white/20 rounded-full px-2.5 py-1 flex items-center gap-2" data-testid={`low-profit-item-${it.id}`}>
                <span className="font-semibold">{it.partName || 'ללא שם'}</span>
                {renderPriceCell(it, labelColorClass)}
                {editingPriceId !== it.id && (
                  <button
                    onClick={() => handleStartEditPrice(it)}
                    title="עדכן מחיר ללקוח"
                    className="text-white/80 hover:text-white"
                    data-testid={`low-profit-edit-${it.id}`}
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                )}
              </span>
            );

            const renderMissingChip = (it) => (
              <span key={it.id} className="text-xs bg-white/20 rounded-full px-2.5 py-1 flex items-center gap-2" data-testid={`low-profit-item-${it.id}`}>
                <span className="font-semibold">{it.partName || 'ללא שם'}</span>
                {editingCostId === it.id ? (
                  <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="number"
                      value={editingCostValue}
                      onChange={(e) => setEditingCostValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCost(it); if (e.key === 'Escape') setEditingCostId(null); }}
                      placeholder="עלות"
                      className="w-20 text-xs text-gray-900 rounded px-1.5 py-0.5 border border-white/40 focus:outline-none"
                      autoFocus
                      data-testid={`low-profit-cost-input-${it.id}`}
                    />
                    <button
                      onClick={() => handleSaveCost(it)}
                      disabled={savingCost}
                      className="text-xs bg-white text-emerald-700 rounded px-2 py-0.5 font-bold hover:bg-emerald-50 disabled:opacity-50"
                      data-testid={`low-profit-cost-save-${it.id}`}
                    >
                      שמור
                    </button>
                    <button
                      onClick={() => setEditingCostId(null)}
                      className="text-xs text-white/80 hover:text-white"
                      data-testid={`low-profit-cost-cancel-${it.id}`}
                    >
                      ביטול
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => handleStartEditCost(it)}
                    title="הזן מחיר עלות"
                    className="flex items-center gap-1 text-white/90 hover:text-white bg-white/10 rounded-full px-2 py-0.5"
                    data-testid={`low-profit-cost-edit-${it.id}`}
                  >
                    <Edit2 className="w-3 h-3" />
                    הזן עלות
                  </button>
                )}
              </span>
            );

            const groupBySupplier = (arr) => {
              const groups = {};
              arr.forEach(it => {
                const key = (it.supplier && it.supplier.trim()) ? it.supplier.trim() : 'ללא ספק';
                (groups[key] = groups[key] || []).push(it);
              });
              return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
            };

            const renderGrouped = (arr, chipRenderer) => (
              <div className="space-y-2">
                {groupBySupplier(arr).map(([supplier, group]) => (
                  <div key={supplier} className="flex flex-wrap items-center gap-2" data-testid={`low-profit-supplier-group-${supplier}`}>
                    <span className="text-xs font-bold bg-white/25 rounded px-2 py-1 whitespace-nowrap">
                      {supplier} ({group.length})
                    </span>
                    {group.map(it => chipRenderer(it))}
                  </div>
                ))}
              </div>
            );

            return (
            <div className="mb-4" data-testid="low-profit-alert-banner">
              <div className="bg-gradient-to-r from-rose-500 to-red-600 rounded-lg shadow-lg p-4 text-white relative animate-pulse-slow">
                <button
                  onClick={() => setLowProfitDismissed(true)}
                  className="absolute top-2 left-2 text-white/70 hover:text-white text-xl font-bold w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                  data-testid="dismiss-low-profit-alert-btn"
                >
                  ×
                </button>
                <div className="flex items-start gap-3">
                  <TrendingDown className="w-8 h-8 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold">
                      התראת רווח נמוך: {lowProfitItems.length} פריטים לטיפול
                    </h3>

                    {belowItems.length > 0 && (
                      <div className="mt-3" data-testid="low-profit-below-section">
                        <p className="text-sm font-semibold text-white/90 mb-1.5">
                          רווח מתחת ל-{lowProfitThreshold}% ({belowItems.length})
                        </p>
                        {renderGrouped(belowItems, (it) => renderChip(it, 'text-yellow-100'))}
                      </div>
                    )}

                    {missingItems.length > 0 && (
                      <div className="mt-3" data-testid="low-profit-missing-section">
                        <p className="text-sm font-semibold text-white/90 mb-1.5">
                          חסר מחיר עלות — להשלמת נתונים ({missingItems.length})
                        </p>
                        {renderGrouped(missingItems, (it) => renderMissingChip(it))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => navigate('/price-list', { state: { workId: '__global__', workName: 'מחירון כללי' } })}
                    className="bg-white text-red-600 px-4 py-2 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors flex-shrink-0"
                    data-testid="alert-view-pricelist-btn"
                  >
                    פתח מחירון
                  </button>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Debtors Ticker */}
          {debtors.length > 0 && (
            <div className="mb-6">
              <div className="bg-white rounded-lg shadow-md p-3 border-t-2 border-red-500 overflow-hidden">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-base font-bold text-gray-800 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    חייבים ({debtors.length})
                  </h2>
                  <Button
                    onClick={() => navigate('/debtors-report')}
                    variant="outline"
                    size="sm"
                    className="border-red-500 text-red-600 hover:bg-red-50 text-xs h-7 px-2"
                    data-testid="go-to-debtors-report"
                  >
                    עבור לדווח חייבים
                  </Button>
                </div>

                <div className="relative h-28 overflow-hidden">
                  <div className="debtors-ticker absolute w-full">
                    {[...debtors, ...debtors].map((debtor, idx) => (
                      <div
                        key={`${debtor.id}-${idx}`}
                        onClick={() => {
                          if (debtor.type === 'project') {
                            navigate(`/project-execution-steps/${debtor.id}`);
                          } else {
                            navigate(`/daily-log?id=${debtor.id}`);
                          }
                        }}
                        className="flex items-center justify-between p-2 mb-2 rounded-md border bg-gradient-to-r from-red-50 to-orange-50 border-red-200 cursor-pointer hover:from-red-100 hover:to-orange-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          <span className="text-sm font-medium text-gray-800">{debtor.name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${debtor.type === 'project' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {debtor.type === 'project' ? 'פרויקט' : 'רישום יומי'}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-red-600">
                          ₪{debtor.debt.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-2 text-xs text-gray-600 flex justify-between items-center bg-red-50 p-2 rounded">
                  <span>סה"כ חייבים: <strong className="text-red-600">{debtors.length}</strong></span>
                  <span>סה"כ חוב: <strong className="text-red-600">₪{debtors.reduce((s, d) => s + d.debt, 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                </div>
              </div>

              <style>{`
                @keyframes debtors-scroll {
                  0% { transform: translateY(0); }
                  100% { transform: translateY(-50%); }
                }
                .debtors-ticker {
                  animation: debtors-scroll 20s linear infinite;
                }
                .debtors-ticker:hover {
                  animation-play-state: paused;
                }
              `}</style>
            </div>
          )}

          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-md p-4 text-center transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-center gap-3">
              <Folder className="w-8 h-8 text-white" />
              <h3 className="text-xl font-bold text-white">תיקיית AIMAN</h3>
              <Button
                onClick={() => window.open('/AIMAN/login.html', '_blank')}
                className="px-4 py-2 text-sm font-bold bg-white text-orange-600 hover:bg-gray-100 shadow-lg transition-all duration-300"
                size="sm"
              >
                פתח
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;